import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Award, Send, Clock, X, PenLine, Check } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Header } from "@/components/shared";
import { listRecommendations, listConnections, requestRecommendation, writeRecommendation, declineRecommendation } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export default function Recommendations() {
  const { currentUser } = useApp();
  const isWorker = currentUser.role === "worker";
  const [tab, setTab] = useState(isWorker ? "mine" : "incoming");
  const [mine, setMine] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [connections, setConnections] = useState([]);
  const [askOpen, setAskOpen] = useState(false);

  const load = async () => {
    const [w, r, c] = await Promise.all([
      isWorker ? listRecommendations("worker") : Promise.resolve([]),
      listRecommendations("reviewer"),
      listConnections(),
    ]);
    setMine(w); setIncoming(r); setConnections(c.filter((x) => x.status === "accepted"));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const pendingIncoming = incoming.filter((r) => r.status === "requested");

  return (
    <div className="min-h-screen grain">
      <Header title="Recommendations" />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-center gap-2 text-primary">
          <Award className="w-5 h-5" />
          <span className="text-xs font-bold tracking-[0.2em] uppercase">Testimonials</span>
        </div>
        <h1 className="mt-1 font-heading text-2xl sm:text-3xl font-extrabold tracking-tight">Recommendations</h1>
        <p className="mt-2 text-foreground/80 max-w-lg">
          {isWorker
            ? "Ask connections to write recommendations. Accepted ones show up on your public resume."
            : "Workers you're connected with can ask you for recommendations. Written ones appear on their resume."}
        </p>

        <div className="mt-6 flex items-center justify-between gap-3 flex-wrap">
          <div className="inline-flex rounded-full border border-border bg-card p-1 flex-wrap">
            {isWorker && (
              <TabBtn testid="rec-tab-mine" active={tab === "mine"} onClick={() => setTab("mine")}>
                My requests ({mine.length})
              </TabBtn>
            )}
            <TabBtn testid="rec-tab-incoming" active={tab === "incoming"} onClick={() => setTab("incoming")}>
              Asked of me
              {pendingIncoming.length > 0 && <span className="ml-1 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200 px-1.5 text-[10px] font-bold">{pendingIncoming.length}</span>}
            </TabBtn>
          </div>
          {isWorker && (
            <Button data-testid="ask-rec-btn" onClick={() => setAskOpen(true)}>
              <Send className="w-4 h-4 mr-1.5" /> Ask for a recommendation
            </Button>
          )}
        </div>

        {tab === "mine" && isWorker && (
          <div className="mt-6 space-y-3">
            {mine.length === 0 && <Empty text="You haven't asked for any recommendations yet." />}
            {mine.map((r) => <RecCard key={r.id} rec={r} me="worker" />)}
          </div>
        )}
        {tab === "incoming" && (
          <div className="mt-6 space-y-3">
            {incoming.length === 0 && <Empty text="No one has asked you yet." />}
            {incoming.map((r) => <RecCard key={r.id} rec={r} me="reviewer" onChange={load} />)}
          </div>
        )}
      </div>

      {isWorker && (
        <AskDialog open={askOpen} onOpenChange={setAskOpen} connections={connections} onDone={() => { setAskOpen(false); load(); }} mine={mine} />
      )}
    </div>
  );
}

const TabBtn = ({ testid, active, onClick, children }) => (
  <button data-testid={testid} onClick={onClick}
          className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold transition-colors ${active ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>
    {children}
  </button>
);

const RecCard = ({ rec, me, onChange }) => {
  const [writing, setWriting] = useState(false);
  const [text, setText] = useState("");
  const [relationship, setRelationship] = useState("");
  const other = me === "worker" ? rec.reviewer : rec.worker;

  const submit = async () => {
    if (!text.trim()) return;
    try {
      await writeRecommendation(rec.id, text, relationship);
      toast.success("Recommendation posted");
      onChange?.();
    } catch { toast.error("Could not post"); }
  };
  const decline = async () => {
    await declineRecommendation(rec.id); toast.info("Declined"); onChange?.();
  };

  return (
    <motion.div layout initial={{ opacity: 0 }} animate={{ opacity: 1 }} data-testid={`rec-${rec.id}`}
                className="rounded-lg border border-border bg-card p-5">
      <div className="flex items-start gap-3">
        <Avatar className="w-12 h-12"><AvatarImage src={other?.avatar_url} className="object-cover" /><AvatarFallback>{other?.name?.[0]}</AvatarFallback></Avatar>
        <div className="min-w-0 flex-1">
          <div className="font-heading font-bold">{other?.name}</div>
          <div className="text-sm text-muted-foreground">{other?.headline || other?.company}</div>
          {rec.context && <div className="text-sm mt-1 italic text-muted-foreground">"{rec.context}"</div>}
        </div>
        <StatusPill status={rec.status} />
      </div>
      {rec.status === "written" && (
        <div className="mt-4 rounded-xl bg-accent p-4 text-sm">
          {rec.relationship && <div className="text-xs uppercase tracking-[0.15em] font-bold text-muted-foreground mb-1">{rec.relationship}</div>}
          <p className="text-foreground/90">"{rec.text}"</p>
        </div>
      )}
      {me === "reviewer" && rec.status === "requested" && !writing && (
        <div className="mt-4 flex gap-2 justify-end">
          <Button data-testid={`rec-decline-${rec.id}`} size="sm" variant="outline" onClick={decline}><X className="w-4 h-4 mr-1" /> Decline</Button>
          <Button data-testid={`rec-write-${rec.id}`} size="sm" onClick={() => setWriting(true)}><PenLine className="w-4 h-4 mr-1" /> Write recommendation</Button>
        </div>
      )}
      {me === "reviewer" && rec.status === "requested" && writing && (
        <div className="mt-4 space-y-2">
          <Input data-testid={`rec-relationship-${rec.id}`} placeholder="Your relationship (e.g. Project client, Manager)"
                 value={relationship} onChange={(e) => setRelationship(e.target.value)} />
          <Textarea data-testid={`rec-text-${rec.id}`} rows={4} placeholder="What was it like to work with them? Be specific."
                    value={text} onChange={(e) => setText(e.target.value)} />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setWriting(false)}>Cancel</Button>
            <Button data-testid={`rec-post-${rec.id}`} size="sm" onClick={submit}><Check className="w-4 h-4 mr-1" /> Post</Button>
          </div>
        </div>
      )}
    </motion.div>
  );
};

const StatusPill = ({ status }) => {
  const map = {
    requested: { text: "Pending", cls: "bg-amber-100 text-amber-800 dark:bg-amber-500/20 dark:text-amber-200", icon: <Clock className="w-3 h-3" /> },
    written: { text: "Posted", cls: "bg-primary/10 text-primary", icon: <Check className="w-3 h-3" /> },
    declined: { text: "Declined", cls: "bg-muted text-muted-foreground", icon: <X className="w-3 h-3" /> },
  };
  const c = map[status] || map.requested;
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold shrink-0 ${c.cls}`}>{c.icon}{c.text}</span>;
};

const Empty = ({ text }) => <div className="mt-10 text-center text-muted-foreground">{text}</div>;

const AskDialog = ({ open, onOpenChange, connections, onDone, mine }) => {
  const [context, setContext] = useState("");
  const [busy, setBusy] = useState(null);
  const alreadyAskedIds = new Set(mine.filter((r) => r.status !== "declined").map((r) => r.reviewer_id));

  const ask = async (u) => {
    setBusy(u.id);
    try {
      const r = await requestRecommendation(u.id, context);
      if (r.created) toast.success(`Request sent to ${u.name}`);
      else toast.info("Already requested");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not send"); }
    setBusy(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="ask-rec-dialog">
        <DialogTitle className="font-heading text-xl font-bold flex items-center gap-2">
          <Send className="w-5 h-5 text-primary" /> Ask for a recommendation
        </DialogTitle>
        <DialogDescription>Pick a connection to write you a recommendation.</DialogDescription>
        <Textarea data-testid="ask-rec-context" rows={2} placeholder="Optional context — what should they highlight?" value={context} onChange={(e) => setContext(e.target.value)} />
        <div className="mt-2 max-h-80 overflow-y-auto space-y-2">
          {connections.length === 0 && <p className="text-sm text-muted-foreground text-center py-6">You need to be connected with someone first.</p>}
          {connections.map((c) => {
            const u = c.other;
            const disabled = alreadyAskedIds.has(u.id);
            return (
              <div key={u.id} className="rounded-xl border border-border p-2.5 flex items-center gap-3">
                <Avatar className="w-9 h-9"><AvatarImage src={u.avatar_url} className="object-cover" /><AvatarFallback>{u.name[0]}</AvatarFallback></Avatar>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold truncate">{u.name}</div>
                  <div className="text-xs text-muted-foreground truncate">{u.headline || u.company}</div>
                </div>
                <Button data-testid={`ask-rec-target-${u.id}`} size="sm" onClick={() => ask(u)} disabled={disabled || busy === u.id}>
                  {disabled ? "Requested" : "Ask"}
                </Button>
              </div>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
};
